import type { AccountingProcessing, LeaseTransactionJudge, OnBalance } from '@libs/enums';
import type { ItemAttributes, ItemNames, OrEmpty } from '@ps-smart-cp/ppfw-client';
import { ItemName, Locale, Numeric, SingleValue } from '@ps-smart-cp/ppfw-client';

interface Schema {
  // 追加借入利子率
  additionalBorrowingInterestRate: string;

  // 当初直接費用
  initialDirectExpenses: string;

  // 延長オプション期間
  extensionOptionPeriodMonths: string;

  // 購入オプション行使価額
  purchaseOptionAmount: string;

  // 前払リース料
  prePaidLeaseFee: string;

  // リース・インセンティブ(前払)
  prePaidLeaseIncentive: string;

  // 変動リース料
  floatingLeaseFee: string;

  // リース・インセンティブ
  leaseIncentive: string;

  // 解約オプション行使価額
  cancelOptionEnforcementAmount: string;

  // リース取引判定
  leaseTransactionJudgement: OrEmpty<LeaseTransactionJudge>;

  // オンバランス
  onBalance: OrEmpty<OnBalance>;

  // 会計処理
  accountingProcess: OrEmpty<AccountingProcessing>;

  // リース債務
  leaseLiability: string;

  // リース期間
  leasePeriod: string;

  // 取得価額相当額
  acquisitionEquivalentValue: string;

  // 使用権資産取得価額
  rightOfUseAssetAcquisitionAmount: string;

  // 適用利子率
  appliedInterestRate: string;

  // 残価保証額(会計)
  guaranteedResidualValueAccount: string;
}

const attributes = {
  additionalBorrowingInterestRate: Numeric.of(0, 1, 7),
  initialDirectExpenses: Numeric.preset.currency(),
  extensionOptionPeriodMonths: Numeric.of(-999, 999, 0),
  purchaseOptionAmount: Numeric.preset.currency(),
  prePaidLeaseFee: Numeric.preset.currency(),
  prePaidLeaseIncentive: Numeric.of(-9_999_999_999_999, 0, 0),
  floatingLeaseFee: Numeric.of(-9_999_999_999_999, 9_999_999_999_999, 0),
  leaseIncentive: Numeric.of(-9_999_999_999_999, 0, 0),
  cancelOptionEnforcementAmount: Numeric.of(-9_999_999_999_999, 9_999_999_999_999, 0),
  leaseTransactionJudgement: SingleValue.shortValue(),
  onBalance: SingleValue.shortValue(),
  accountingProcess: SingleValue.shortValue(),
  leaseLiability: Numeric.of(-9_999_999_999_999, 9_999_999_999_999, 0),
  leasePeriod: Numeric.of(-999, 999, 0),
  guaranteedResidualValueAccount: Numeric.preset.currency(),
} satisfies ItemAttributes<Schema>;

const names = {
  additionalBorrowingInterestRate: ItemName.of([Locale.ja, '追加借入利子率']),
  initialDirectExpenses: ItemName.of([Locale.ja, '当初直接費用']),
  extensionOptionPeriodMonths: ItemName.of([Locale.ja, '延長オプション期間']),
  purchaseOptionAmount: ItemName.of([Locale.ja, '購入オプション行使価額']),
  prePaidLeaseFee: ItemName.of([Locale.ja, '前払リース料']),
  prePaidLeaseIncentive: ItemName.of([Locale.ja, 'リース・インセンティブ(前払)']),
  floatingLeaseFee: ItemName.of([Locale.ja, '変動リース料']),
  leaseIncentive: ItemName.of([Locale.ja, 'リース・インセンティブ']),
  cancelOptionEnforcementAmount: ItemName.of([Locale.ja, '解約オプション行使価額']),
  leaseTransactionJudgement: ItemName.of([Locale.ja, 'リース取引判定']),
  onBalance: ItemName.of([Locale.ja, 'オンバランス']),
  accountingProcess: ItemName.of([Locale.ja, '会計処理']),
  leasePeriod: ItemName.of([Locale.ja, 'リース期間']),
  guaranteedResidualValueAccount: ItemName.of([Locale.ja, '残価保証額(会計)']),
} satisfies ItemNames<Schema>;

const labelNames = {
  netLeasePaymentFee: ItemName.of([Locale.ja, 'NET支払リース料']),
  acquisitionEquivalentValue: ItemName.of([Locale.ja, '取得価額相当額']),
  rightOfUseAssetAcquisitionAmount: ItemName.of([Locale.ja, '使用権資産取得価額']),
  appliedInterestRate: ItemName.of([Locale.ja, '適用利子率']),
  totalLeaseFeeAccount: ItemName.of([Locale.ja, 'リース料総額(会計)']),
  totalLeaseFeeBeforeDeduction: ItemName.of([Locale.ja, 'リース料総額(控除前)']),
  totalFloatingLeaseFee: ItemName.of([Locale.ja, '変動リース料合計']),
  totalLeaseIncentive: ItemName.of([Locale.ja, 'リース・インセンティブ合計']),
  totalCancelOptionEnforcementAmount: ItemName.of([Locale.ja, '解約オプション行使価額合計']),
  paymentSchedule: ItemName.of([Locale.ja, '支払スケジュール']),
  autoJudgeInfo: ItemName.of([Locale.ja, '自動判定情報']),
  autoJudgeResultInfo: ItemName.of([Locale.ja, '自動判定結果情報']),
  reAutoJudgeInfo: ItemName.of([Locale.ja, '再測定情報']),
  reAutoJudgeResultInfo: ItemName.of([Locale.ja, '再測定結果情報']),
  judgeResultInfo: ItemName.of([Locale.ja, '判定結果情報']),
  laAssetInfo: ItemName.of([Locale.ja, 'リース資産情報']),
  contractInfoConfirm: ItemName.of([Locale.ja, '契約情報確認']),
};

interface Record {
  additionalBorrowingInterestRate: string;
  initialDirectExpenses: string;
  extensionOptionPeriodMonths: string;
  purchaseOptionAmount: string;
  prePaidLeaseFee: string;
  prePaidLeaseIncentive: string;
  floatingLeaseFee: string;
  leaseIncentive: string;
  cancelOptionEnforcementAmount: string;
  leaseTransactionJudgement: string;
  onBalance: string;
  accountingProcess: string;
  acquisitionEquivalentValue: string;
  rightOfUseAssetAcquisitionAmount: string;
  appliedInterestRate: string;
  guaranteedResidualValueAccount: string;
}

const autoJudgeInfo = {
  names,
  labelNames,
  attributes,
};

interface AutoJudgeInfo {
  schema: Schema;
  record: Record;
}

export { autoJudgeInfo };
export type { AutoJudgeInfo };
